import { useState, memo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Share2,
  Download,
  RefreshCw,
  Edit3,
  Check,
  X,
  Loader2,
} from 'lucide-react';
import { toast } from '../../../../../utils/toast';

/**
 * Top toolbar for the itinerary view.
 * Handles: back navigation, trip name editing, share, download, regenerate.
 */
function ItineraryToolbar({
  tripName,
  tripId,
  onTripNameChange,
  onRegenerate,
  regenerating = false,
}) {
  const navigate = useNavigate();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(tripName || '');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!draft.trim() || draft.trim() === tripName) {
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      const res = await fetch(
        `${import.meta.env.VITE_API_URL}/api/trips/${tripId}`,
        {
          method: 'PUT',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ tripName: draft.trim() }),
        },
      );
      if (!res.ok) throw new Error('Failed to update trip name');
      onTripNameChange?.(draft.trim());
      toast.success('Trip name updated');
    } catch {
      toast.error('Failed to update trip name');
    } finally {
      setSaving(false);
      setEditing(false);
    }
  };

  const handleShare = async () => {
    const clientUrl = import.meta.env.VITE_CLIENT_PORTAL_URL || import.meta.env.VITE_CLIENT_APP_URL;
    const link = clientUrl
      ? `${clientUrl}?tripId=${tripId}`
      : `${window.location.origin}/agent/itinerary/view/${tripId}`;
    try {
      await navigator.clipboard.writeText(link);
      toast.success('Share link copied to clipboard');
    } catch {
      toast.error('Failed to copy link');
    }
  };

  const handleDownload = () => {
    window.print();
  };

  return (
    <div className="sticky top-0 z-sticky bg-white/95 border-b border-gray-200 backdrop-blur-sm print:hidden">
      <div className="flex gap-3 justify-between items-center px-4 py-3 mx-auto max-w-5xl sm:px-6">
        {/* Left: Back + Title */}
        <div className="flex gap-3 items-center min-w-0">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="flex-shrink-0 p-2 text-gray-500 rounded-lg transition-colors hover:text-gray-700 hover:bg-gray-100"
            title="Go back"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>

          {editing ? (
            <div className="flex gap-2 items-center">
              <input
                type="text"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSave();
                  if (e.key === 'Escape') { setEditing(false); setDraft(tripName); }
                }}
                className="px-2 py-1 text-sm font-semibold rounded border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                autoFocus
              />
              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                className="p-1 text-green-600 rounded hover:bg-green-50"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
              </button>
              <button
                type="button"
                onClick={() => { setEditing(false); setDraft(tripName); }}
                className="p-1 text-gray-400 rounded hover:bg-gray-100"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => { setDraft(tripName); setEditing(true); }}
              className="flex gap-1.5 items-center min-w-0 group"
              title="Edit trip name"
            >
              <h1 className="text-base font-semibold text-gray-900 truncate sm:text-lg">
                {tripName || 'Itinerary'}
              </h1>
              <Edit3 className="flex-shrink-0 w-3.5 h-3.5 text-gray-400 opacity-0 transition-opacity group-hover:opacity-100" />
            </button>
          )}
        </div>

        {/* Right: Actions */}
        <div className="flex gap-1 items-center sm:gap-2">
          <button
            type="button"
            onClick={handleShare}
            className="flex gap-1.5 items-center px-3 py-2 text-sm font-medium text-gray-700 rounded-lg border border-gray-200 transition-colors hover:bg-gray-50"
            title="Copy share link"
          >
            <Share2 className="w-4 h-4" />
            <span className="hidden sm:inline">Share</span>
          </button>
          <button
            type="button"
            onClick={handleDownload}
            className="flex gap-1.5 items-center px-3 py-2 text-sm font-medium text-gray-700 rounded-lg border border-gray-200 transition-colors hover:bg-gray-50"
            title="Print / Download"
          >
            <Download className="w-4 h-4" />
            <span className="hidden sm:inline">Print</span>
          </button>
          {onRegenerate && (
            <button
              type="button"
              onClick={onRegenerate}
              disabled={regenerating}
              className="flex gap-1.5 items-center px-3 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg transition-colors hover:bg-blue-700 disabled:opacity-50"
              title="Regenerate itinerary"
            >
              {regenerating ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <RefreshCw className="w-4 h-4" />
              )}
              <span className="hidden sm:inline">{regenerating ? 'Regenerating...' : 'Regenerate'}</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default memo(ItineraryToolbar);
